/**
 * Shared card primitives for the supporter role dashboards.
 * Reuse the Gov design system (GovCard/GovEmptyState) + premium AnimatedCounter.
 * No financial concepts anywhere — campaign cards deliberately omit any amount.
 */
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Bookmark, BookmarkCheck, Share2, ExternalLink, Megaphone, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { GovCard, GovEmptyState } from '@/components/layout/GovLayout'
import { AnimatedCounter } from '@/components/ui/data-display'
import type { Resource } from '@/services/resourceService'
import type { Campaign } from '@/services/campaignService'

// ──────────────────────────────────────────────────────────────────────────
// Stat card — real numbers only; "Data required" where no source exists.
// ──────────────────────────────────────────────────────────────────────────
export function SupporterStatCard({
  icon: Icon,
  value,
  labelKey,
  label,
  dataRequired,
  accent = 'bg-blue-50 text-blue-600',
}: {
  icon: React.ComponentType<{ className?: string }>
  value: number
  labelKey?: string
  label?: string
  dataRequired?: boolean
  accent?: string
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn('text-2xl font-bold', dataRequired ? 'text-gray-400' : 'text-gray-900')}>
            {dataRequired ? '—' : <AnimatedCounter value={value} duration={600} />}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {labelKey ? t(labelKey, label ?? '') : label}
          </p>
        </div>
        <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0', accent)}>
          <Icon className="w-5 h-5" aria-hidden="true" />
        </div>
      </div>
      {dataRequired && (
        <span className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
          {t('supporter.metrics.dataRequired', 'Data required')}
        </span>
      )}
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Section card — titled wrapper for a dashboard section.
// ──────────────────────────────────────────────────────────────────────────
export function SupporterSectionCard({
  title,
  description,
  icon: Icon,
  action,
  children,
  className,
}: {
  title: string
  description?: string
  icon?: React.ComponentType<{ className?: string }>
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <GovCard className={className}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {Icon && (
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
            {description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{description}</p>}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Resource card — Read / Save / Share (no price, no checkout).
// ──────────────────────────────────────────────────────────────────────────
export function SupporterResourceCard({
  resource,
  saved,
  onToggleSave,
  onShare,
}: {
  resource: Resource
  saved?: boolean
  onToggleSave?: (r: Resource) => void
  onShare?: (r: Resource) => void
}) {
  const { t } = useTranslation()
  const href = resource.externalUrl || resource.fileUrl
  const isVideo = resource.resourceType === 'VIDEO'
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'w-11 h-11 rounded-xl flex items-center justify-center shrink-0',
            isVideo ? 'bg-rose-50 text-rose-600' : 'bg-cyan-50 text-cyan-600',
          )}
        >
          {isVideo ? <Share2 className="w-5 h-5" /> : <ExternalLink className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-gray-900 truncate">{resource.title}</h4>
          {resource.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{resource.description}</p>}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400 flex-wrap">
            {resource.sourceOrg && <span className="truncate max-w-[140px]">{resource.sourceOrg}</span>}
            {resource.language && <span>· {resource.language}</span>}
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline inline-flex items-center gap-1 min-h-[40px]"
              >
                {isVideo ? t('supporter.actions.watch', 'Watch') : t('supporter.actions.read', 'Read')}
                <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            )}
            {onToggleSave && (
              <button
                onClick={() => onToggleSave(resource)}
                className="text-sm font-medium hover:underline inline-flex items-center gap-1 min-h-[40px] disabled:opacity-50"
                style={{ color: saved ? '#059669' : '#4b5563' }}
              >
                {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {saved ? t('supporter.actions.saved', 'Saved') : t('supporter.actions.save', 'Save')}
              </button>
            )}
            {onShare && (
              <button
                onClick={() => onShare(resource)}
                className="text-sm font-medium text-gray-600 hover:underline inline-flex items-center gap-1 min-h-[40px]"
              >
                <Share2 className="w-4 h-4" aria-hidden="true" />
                {t('supporter.actions.share', 'Share')}
              </button>
            )}
          </div>
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Campaign card — Join / Learn More / Share (non-financial; no amounts shown).
// ──────────────────────────────────────────────────────────────────────────
export function SupporterCampaignCard({
  campaign,
  joined,
  onJoin,
  onShare,
  onLearnMore,
}: {
  campaign: Campaign
  joined?: boolean
  onJoin?: (c: Campaign) => void
  onShare?: (c: Campaign) => void
  onLearnMore?: (c: Campaign) => void
}) {
  const { t } = useTranslation()
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Megaphone className="w-5 h-5 text-emerald-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-gray-900 truncate">{campaign.title}</h4>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 capitalize">
              {campaign.status}
            </span>
          </div>
          {campaign.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{campaign.description}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            {campaign.location && <span className="truncate max-w-[140px]">{campaign.location}</span>}
            {campaign.startDate && <span>· {new Date(campaign.startDate).toLocaleDateString('en-IN')}</span>}
            {campaign.actualParticipants != null && <span>· {campaign.actualParticipants} joined</span>}
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            {onJoin && (
              <button
                onClick={() => onJoin(campaign)}
                disabled={joined}
                className="text-sm font-medium text-blue-600 hover:underline inline-flex items-center gap-1 min-h-[40px] disabled:opacity-50"
              >
                {joined ? t('supporter.actions.joined', 'Joined') : t('supporter.actions.join', 'Join Campaign')}
              </button>
            )}
            {onLearnMore && (
              <button
                onClick={() => onLearnMore(campaign)}
                className="text-sm font-medium text-gray-600 hover:underline inline-flex items-center gap-1 min-h-[40px]"
              >
                {t('supporter.actions.learnMore', 'Learn More')}
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            )}
            {onShare && (
              <button
                onClick={() => onShare(campaign)}
                className="text-sm font-medium text-gray-600 hover:underline inline-flex items-center gap-1 min-h-[40px]"
              >
                <Share2 className="w-4 h-4" aria-hidden="true" />
                {t('supporter.actions.share', 'Share')}
              </button>
            )}
          </div>
        </div>
      </div>
    </GovCard>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Empty state — wraps GovEmptyState with a CTA.
// ──────────────────────────────────────────────────────────────────────────
export function SupporterEmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  icon: string
  title: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}) {
  const action = actionLabel && onAction ? { label: actionLabel, onClick: onAction } : undefined
  return <GovEmptyState icon={icon} title={title} description={description} action={action} />
}